import { useState, useEffect } from 'react';
import { DropResult } from 'react-beautiful-dnd';

import { ViewerItem } from 'presentations/api/schema/user';
import { apiPath } from 'presentations/routes/path';
import { TeamUserListItem } from 'presentations/api/schema/team_user';
import {
  setPersistentItemWithViewer,
  defaultCacheExpiration,
} from 'presentations/views/helpers/storage';
import { TeamUserOrderSyncBody } from 'presentations/api/schema/team_user_order';
import { patch } from 'infras/rest_client';
import { SortType } from 'domains/models/team_user_order/sortType';

export const selectableUserUidsKey = 'selectableUserUids';

export interface Props {
  viewer: ViewerItem;
  viewerTeamUser?: TeamUserListItem;
  selectableUserUids: string[];
  selectableTeamUsers: TeamUserListItem[];
  onChangeUserSort: (sortedUsers: TeamUserListItem[]) => void;
  onChangeUserAdd: (newUser: TeamUserListItem) => void;
  onChangeUserDelete: Function;
  onClickInvite?: any;
}

export function useDashboardCalendarCheckList(props: Props) {
  const [isRequesting, setIsRequesting] = useState<boolean>(false);
  const [teamUsers, setTeamUsers] = useState<TeamUserListItem[]>([]);

  useEffect(() => {
    setTeamUsers(props.selectableTeamUsers);
  }, [props.selectableTeamUsers]);

  const dispatchNewSelectableUserUids = (
    newUids: string[],
    targetUids: string[],
  ) => {
    props.onChangeUserDelete(newUids, targetUids);
    setPersistentItemWithViewer(
      selectableUserUidsKey,
      newUids,
      defaultCacheExpiration,
      props.viewer,
    );
  };

  const onDeleteUser = (uid: string) => {
    let newUids: string[];
    if (props.selectableUserUids.some((u) => u === uid)) {
      newUids = props.selectableUserUids.filter((u) => u !== uid);
    } else {
      newUids = [...props.selectableUserUids, uid];
    }
    const newTeamUsers = teamUsers.filter((u) => u.userId !== uid);
    setTeamUsers(newTeamUsers);
    dispatchNewSelectableUserUids(newUids, [uid]);
  };

  const onDragStart = () => {};
  const onDragEnd = async (result: DropResult) => {
    if (!result.destination) {
      return;
    }

    setIsRequesting(true);

    const sortedUsers = [
      ...props.selectableTeamUsers.filter(
        (u) => u.userId !== props.viewerTeamUser?.userId,
      ),
    ];
    const [moved] = sortedUsers.splice(result.source.index, 1);
    sortedUsers.splice(result.destination.index, 0, moved);

    // viewerは先頭固定
    if (props.viewerTeamUser) {
      sortedUsers.unshift(props.viewerTeamUser);
    }

    setTeamUsers(sortedUsers);
    try {
      const users = sortedUsers.map((u, index) => {
        return {
          userId: u.userId,
          sort: index,
        };
      });
      const data: TeamUserOrderSyncBody = {
        sortType: SortType.Calendar,
        users,
      };
      await patch(
        apiPath(`/v1/teams/${props.viewer.team?.uid}/team_user_orders/sync`),
        {
          data,
        },
      );

      // ページ側にも反映
      props.onChangeUserSort(sortedUsers);

      setIsRequesting(false);
    } catch (error) {
      setIsRequesting(false);
    }
  };

  // オプションを選択した時に呼ばれるcallback
  const onSelectNewUser = async (newUser: TeamUserListItem) => {
    const newUsers: TeamUserListItem[] = [];
    // viewerは先頭固定
    if (props.viewerTeamUser) {
      newUsers.push(props.viewerTeamUser);
    }
    newUsers.push(
      ...props.selectableTeamUsers.filter(
        (u) => u.userId !== props.viewerTeamUser?.userId,
      ),
      newUser,
    );
    setTeamUsers(newUsers);

    setIsRequesting(true);
    try {
      const data: TeamUserOrderSyncBody = {
        sortType: SortType.Calendar,
        users: newUsers.map((u, index) => {
          return {
            userId: u.userId,
            sort: index,
          };
        }),
      };
      await patch(
        apiPath(`/v1/teams/${props.viewer.team?.uid}/team_user_orders/sync`),
        {
          data,
        },
      );

      // 先にページ側に反映
      props.onChangeUserAdd(newUser);

      setIsRequesting(false);
    } catch (error) {
      // todo
    }
  };

  return {
    state: {
      isRequesting,
      teamUsers,
    },
    actions: {
      onDeleteUser,
      onDragStart,
      onDragEnd,
      onSelectNewUser,
    },
  };
}
